import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Printer, Star } from 'lucide-react';

// 5-Star Arc SVG Component
const StarArc = ({ size = 'md', color = 'gradient' }) => {
  const sizes = {
    sm: { width: 80, height: 40 },
    md: { width: 120, height: 55 },
    lg: { width: 160, height: 75 }
  };
  const { width, height } = sizes[size];
  
  return (
    <svg width={width} height={height} viewBox="0 0 120 55" fill="none">
      <defs>
        <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#22D3EE" />
          <stop offset="50%" stopColor="#818CF8" />
          <stop offset="100%" stopColor="#C084FC" />
        </linearGradient>
        <filter id="starGlow">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feFlood floodColor="#22D3EE" floodOpacity="0.6"/>
          <feComposite in2="blur" operator="in" result="glow"/>
          <feMerge>
            <feMergeNode in="glow"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Arc path */}
      <path
        d="M 10 45 Q 60 5, 110 45"
        stroke={color === 'gold' ? '#D4AF37' : 'url(#starGradient)'}
        strokeWidth="1.5"
        fill="none"
        opacity="0.6"
      />
      
      {/* 5 Stars */}
      {[
        { x: 10, y: 45, s: 5 },
        { x: 35, y: 22, s: 6 },
        { x: 60, y: 10, s: 7 },
        { x: 85, y: 22, s: 6 },
        { x: 110, y: 45, s: 5 },
      ].map((star, i) => (
        <g key={i} filter="url(#starGlow)">
          <circle 
            cx={star.x} 
            cy={star.y} 
            r={star.s * 0.3} 
            fill="#FFFFFF" 
          />
          <path
            d={`M ${star.x} ${star.y - star.s} L ${star.x + star.s * 0.3} ${star.y - star.s * 0.3} L ${star.x + star.s} ${star.y} L ${star.x + star.s * 0.3} ${star.y + star.s * 0.3} L ${star.x} ${star.y + star.s} L ${star.x - star.s * 0.3} ${star.y + star.s * 0.3} L ${star.x - star.s} ${star.y} L ${star.x - star.s * 0.3} ${star.y - star.s * 0.3} Z`}
            fill={color === 'gold' ? '#D4AF37' : 'url(#starGradient)'}
          />
        </g>
      ))}
    </svg>
  );
};

// Visiting Card - Front
const VisitingCardFront = () => (
  <div 
    data-testid="visiting-card-front"
    className="relative overflow-hidden"
    style={{
      width: '600px',
      height: '343px',
      background: 'linear-gradient(135deg, #1a1f35 0%, #0f1419 100%)',
      borderRadius: '12px',
      border: '1px solid rgba(212, 175, 55, 0.3)',
      boxShadow: '0 0 30px rgba(34, 211, 238, 0.15), 0 0 60px rgba(192, 132, 252, 0.1)',
    }}
  >
    {/* Subtle starfield texture overlay */}
    <div 
      className="absolute inset-0 opacity-10"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1623488226096-7bb98073b27b?w=800)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
    
    {/* Gold border glow */}
    <div 
      className="absolute inset-0 pointer-events-none"
      style={{
        border: '1px solid transparent',
        borderImage: 'linear-gradient(135deg, #D4AF37 0%, transparent 50%, #D4AF37 100%) 1',
        borderRadius: '12px',
      }}
    />
    
    {/* Content */}
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      {/* Star Arc */}
      <div className="mb-4">
        <StarArc size="lg" color="gold" />
      </div>
      
      {/* Brand Name */}
      <h1 
        style={{
          fontFamily: "'Dancing Script', cursive",
          fontSize: '56px',
          fontWeight: 700,
          background: 'linear-gradient(90deg, #22D3EE 0%, #60A5FA 25%, #818CF8 50%, #A78BFA 75%, #C084FC 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: '0.02em',
          textShadow: '0 0 40px rgba(34, 211, 238, 0.5)',
          marginBottom: '8px',
        }}
      >
        Taraanaya
      </h1>
      
      {/* Primary Tagline */}
      <p 
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: '14px',
          fontWeight: 500,
          letterSpacing: '0.3em',
          color: '#D4AF37',
          textTransform: 'uppercase',
          marginBottom: '6px',
        }}
      >
        Decision Intelligence
      </p>
      
      {/* Secondary Tagline */}
      <p 
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '11px',
          fontWeight: 400,
          letterSpacing: '0.15em',
          color: 'rgba(255, 255, 255, 0.6)',
          fontStyle: 'italic',
        }}
      >
        Powered by Astrology
      </p>
      
      {/* Elegant underline */}
      <div 
        className="mt-6"
        style={{
          width: '120px',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, #D4AF37 50%, transparent)',
        }}
      />
    </div>
  </div>
);

// Visiting Card - Back
const VisitingCardBack = () => (
  <div 
    data-testid="visiting-card-back"
    className="relative overflow-hidden"
    style={{
      width: '600px',
      height: '343px',
      background: 'linear-gradient(135deg, #1a1f35 0%, #0f1419 100%)',
      borderRadius: '12px',
      border: '1px solid rgba(212, 175, 55, 0.3)',
      boxShadow: '0 0 30px rgba(34, 211, 238, 0.15), 0 0 60px rgba(192, 132, 252, 0.1)',
    }}
  >
    {/* Subtle texture */}
    <div 
      className="absolute inset-0 opacity-5"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1623488226096-7bb98073b27b?w=800)',
        backgroundSize: 'cover',
      }}
    />
    
    {/* Content */}
    <div className="relative z-10 flex h-full">
      {/* Left Section - Branding */}
      <div className="flex flex-col justify-center items-center w-2/5 border-r border-white/10 px-6">
        <StarArc size="sm" color="gradient" />
        <h2 
          style={{
            fontFamily: "'Dancing Script', cursive",
            fontSize: '28px',
            fontWeight: 700,
            background: 'linear-gradient(90deg, #22D3EE, #C084FC)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginTop: '12px',
          }}
        >
          Taraanaya
        </h2>
        <p 
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '8px',
            letterSpacing: '0.2em',
            color: '#D4AF37',
            textTransform: 'uppercase',
            marginTop: '4px',
          }}
        >
          Vedtech Pvt. Ltd.
        </p>
      </div>
      
      {/* Right Section - Contact Details */}
      <div className="flex flex-col justify-center w-3/5 px-8 py-6">
        {/* Founders */}
        <div className="mb-6">
          <p 
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '16px',
              fontWeight: 600,
              color: '#FFFFFF',
              marginBottom: '2px',
            }}
          >
            Mandeep Singh & Parveen Kaur
          </p>
          <p 
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '10px',
              color: '#D4AF37',
              letterSpacing: '0.1em',
            }}
          >
            Co-Founders
          </p>
        </div>
        
        {/* Contact Info */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 flex items-center justify-center">
              <span className="text-[8px] text-white">@</span>
            </div>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: '#B8B8D1' }}>
              contact@taraanaya.com
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 flex items-center justify-center">
              <span className="text-[8px] text-white">W</span>
            </div>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: '#B8B8D1' }}>
              www.taraanaya.com
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 flex items-center justify-center">
              <span className="text-[8px] text-white">Ph</span>
            </div>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: '#B8B8D1' }}>
              +91 XXXXX XXXXX
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Identity Card Component
const IdentityCard = ({ name, designation, employeeId, photoPlaceholder = true }) => (
  <div 
    data-testid={`id-card-${name.toLowerCase().replace(' ', '-')}`}
    className="relative overflow-hidden"
    style={{
      width: '340px',
      height: '540px',
      background: 'linear-gradient(180deg, #1a1f35 0%, #0f1419 100%)',
      borderRadius: '16px',
      border: '1px solid rgba(212, 175, 55, 0.4)',
      boxShadow: '0 0 40px rgba(34, 211, 238, 0.2), 0 0 80px rgba(192, 132, 252, 0.15)',
    }}
  >
    {/* Background texture */}
    <div 
      className="absolute inset-0 opacity-8"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1623488226096-7bb98073b27b?w=600)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
    
    {/* Gold corner accents */}
    <div className="absolute top-0 left-0 w-16 h-16">
      <div className="absolute top-2 left-2 w-12 h-[1px] bg-gradient-to-r from-[#D4AF37] to-transparent" />
      <div className="absolute top-2 left-2 w-[1px] h-12 bg-gradient-to-b from-[#D4AF37] to-transparent" />
    </div>
    <div className="absolute top-0 right-0 w-16 h-16">
      <div className="absolute top-2 right-2 w-12 h-[1px] bg-gradient-to-l from-[#D4AF37] to-transparent" />
      <div className="absolute top-2 right-2 w-[1px] h-12 bg-gradient-to-b from-[#D4AF37] to-transparent" />
    </div>
    <div className="absolute bottom-0 left-0 w-16 h-16">
      <div className="absolute bottom-2 left-2 w-12 h-[1px] bg-gradient-to-r from-[#D4AF37] to-transparent" />
      <div className="absolute bottom-2 left-2 w-[1px] h-12 bg-gradient-to-t from-[#D4AF37] to-transparent" />
    </div>
    <div className="absolute bottom-0 right-0 w-16 h-16">
      <div className="absolute bottom-2 right-2 w-12 h-[1px] bg-gradient-to-l from-[#D4AF37] to-transparent" />
      <div className="absolute bottom-2 right-2 w-[1px] h-12 bg-gradient-to-t from-[#D4AF37] to-transparent" />
    </div>
    
    {/* Content */}
    <div className="relative z-10 flex flex-col items-center h-full pt-8 pb-6 px-6">
      {/* Header - Company */}
      <div className="text-center mb-6">
        <StarArc size="sm" color="gradient" />
        <h3 
          style={{
            fontFamily: "'Dancing Script', cursive",
            fontSize: '32px',
            fontWeight: 700,
            background: 'linear-gradient(90deg, #22D3EE, #C084FC)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginTop: '8px',
          }}
        >
          Taraanaya
        </h3>
        <p 
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '8px',
            letterSpacing: '0.25em',
            color: 'rgba(255, 255, 255, 0.5)',
            textTransform: 'uppercase',
            marginTop: '2px',
          }}
        >
          Vedtech Private Limited
        </p>
      </div>
      
      {/* Divider */}
      <div 
        className="w-full mb-6"
        style={{
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.5), transparent)',
        }}
      />
      
      {/* Photo Placeholder */}
      <div 
        className="relative mb-6"
        style={{
          width: '140px',
          height: '140px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #22D3EE, #818CF8, #C084FC)',
          padding: '4px',
        }}
      >
        <div 
          className="w-full h-full rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(180deg, #1a1f35, #0f1419)',
          }}
        >
          {photoPlaceholder ? (
            <div className="text-center">
              <div 
                className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center"
              >
                <span style={{ fontSize: '28px', color: 'rgba(255,255,255,0.3)' }}>
                  {name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <p className="text-[8px] text-white/30 mt-2">PHOTO</p>
            </div>
          ) : null}
        </div>
      </div>
      
      {/* Name */}
      <h2 
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '24px',
          fontWeight: 600,
          color: '#FFFFFF',
          textAlign: 'center',
          marginBottom: '4px',
        }}
      >
        {name}
      </h2>
      
      {/* Designation */}
      <p 
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: '12px',
          fontWeight: 500,
          color: '#D4AF37',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          marginBottom: '16px',
        }}
      >
        {designation}
      </p>
      
      {/* Employee ID */}
      <div 
        className="px-4 py-2 rounded-full"
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <p 
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '10px',
            color: 'rgba(255, 255, 255, 0.6)',
            letterSpacing: '0.1em',
          }}
        >
          ID: {employeeId}
        </p>
      </div>
      
      {/* Spacer */}
      <div className="flex-1" />
      
      {/* Footer - Tagline */}
      <div className="text-center">
        <p 
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '9px',
            letterSpacing: '0.2em',
            color: 'rgba(255, 255, 255, 0.4)',
            textTransform: 'uppercase',
          }}
        >
          Decision Intelligence
        </p>
        <p 
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '10px',
            fontStyle: 'italic',
            color: 'rgba(255, 255, 255, 0.3)',
            marginTop: '2px',
          }}
        >
          Powered by Astrology
        </p>
      </div>
    </div>
  </div>
);

// Main Brand Materials Page
const BrandMaterials = () => {
  const navigate = useNavigate();
  const printRef = useRef(null);
  
  const handlePrint = () => {
    window.print();
  };
  
  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(180deg, #0a0d14 0%, #12151f 100%)',
      }}
    >
      {/* Google Fonts */}
      <link 
        href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&family=Inter:wght@300;400;500;600&family=Playfair+Display:wght@400;600;700&display=swap" 
        rel="stylesheet" 
      />
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">Back</span>
          </button>
          
          <h1 
            className="text-lg font-medium"
            style={{ 
              fontFamily: "'Inter', sans-serif",
              color: '#D4AF37',
            }}
          >
            Brand Materials
          </h1>
          
          <button 
            data-testid="print-button"
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 text-cyan-400 hover:border-cyan-400 transition-all"
          >
            <Printer size={16} />
            <span className="text-sm">Print All</span>
          </button>
        </div>
      </header>
      
      {/* Content */}
      <main ref={printRef} className="max-w-7xl mx-auto px-6 py-12">
        {/* Visiting Card Section */}
        <section className="mb-16">
          <h2 
            className="text-2xl mb-8 flex items-center gap-3"
            style={{ 
              fontFamily: "'Playfair Display', serif",
              color: '#FFFFFF',
            }}
          >
            <Star className="text-[#D4AF37]" size={24} />
            Company Visiting Card
          </h2>
          
          <div className="flex flex-wrap gap-8 justify-center lg:justify-start">
            {/* Front */}
            <div>
              <p className="text-xs text-white/40 mb-3 uppercase tracking-wider text-center">Front</p>
              <VisitingCardFront />
            </div>
            
            {/* Back */}
            <div>
              <p className="text-xs text-white/40 mb-3 uppercase tracking-wider text-center">Back</p>
              <VisitingCardBack />
            </div>
          </div>
        </section>
        
        {/* ID Cards Section */}
        <section>
          <h2 
            className="text-2xl mb-8 flex items-center gap-3"
            style={{ 
              fontFamily: "'Playfair Display', serif",
              color: '#FFFFFF',
            }}
          >
            <Star className="text-[#D4AF37]" size={24} />
            Co-Founder Identity Cards
          </h2>
          
          <div className="flex flex-wrap gap-8 justify-center lg:justify-start">
            <div>
              <p className="text-xs text-white/40 mb-3 uppercase tracking-wider text-center">Mandeep Singh</p>
              <IdentityCard 
                name="Mandeep Singh"
                designation="Co-Founder"
                employeeId="FOUNDER-01"
              />
            </div>
            
            <div>
              <p className="text-xs text-white/40 mb-3 uppercase tracking-wider text-center">Parveen Kaur</p>
              <IdentityCard 
                name="Parveen Kaur"
                designation="Co-Founder"
                employeeId="FOUNDER-02"
              />
            </div>
          </div>
        </section>
      </main>
      
      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          header, button {
            display: none !important;
          }
          main {
            padding: 0 !important;
          }
          section {
            page-break-inside: avoid;
            margin-bottom: 40px !important;
          }
          h2 {
            color: #1a1f35 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BrandMaterials;
